export const GADGET_COLORS = ["#c084fc", "#67e8f9", "#fbbf24", "#86efac", "#f9a8d4", "#fb923c"];

export const newGadgetId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const localDay = (date = new Date()) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const readDashboardStorage = (key, fallback) => {
    try {
        const value = JSON.parse(localStorage.getItem(key));
        return value ?? fallback;
    } catch {
        return fallback;
    }
};

export const eventKey = (event) => `${event.child_id}:${event.event_id ?? event.id}`;

export const normalizeDashboardEvent = (event) => {
    const value = { ...event.data, ...Object.fromEntries(Object.entries(event).filter(([, item]) => item != null)) };
    return { ...value, id: value.event_id ?? value.id,
        child_id: String(value.child_id ?? value.childId ?? ""),
        subject_id: String(value.subject_id ?? value.subjectId ?? value.class_id ?? value.classId ?? "") };
};

// Resolve old class IDs only within the same child and only for a unique name.
export const reconcileClassroomEvents = (events, subjects, legacySubjects = [], children = [], legacyChildren = []) => events.map(normalizeDashboardEvent).map((original) => {
    let event = original;
    if (!children.some((child) => String(child.child_id) === event.child_id)) {
        const legacyChild = legacyChildren.find((child) => String(child.child_id) === event.child_id);
        const name = String(event.child_name || legacyChild?.name || "").trim().toLowerCase();
        const matches = children.filter((child) => String(child.name || "").trim().toLowerCase() === name && name);
        if (matches.length === 1) event = { ...event, child_id: String(matches[0].child_id), updated_at: new Date().toISOString() };
    }
    const own = subjects.filter((subject) => String(subject.child_id) === event.child_id);
    if (own.some((subject) => String(subject.subject_id ?? subject.id) === event.subject_id)) return event;
    const legacy = legacySubjects.find((subject) => String(subject.subject_id ?? subject.id) === event.subject_id && [event.child_id, original.child_id].includes(String(subject.child_id)));
    const name = String(event.subject_name || legacy?.name || "").trim().toLowerCase();
    const matches = own.filter((subject) => String(subject.name).trim().toLowerCase() === name);
    return matches.length === 1 ? { ...event, subject_id: String(matches[0].subject_id ?? matches[0].id), subject_name: matches[0].name, updated_at: new Date().toISOString() } : event;
});

export const classroomEventOnDate = (raw, date) => {
    const event = normalizeDashboardEvent(raw);
    if (event.archived || event.is_archived || event.deleted_from_dashboard || ["archived", "cancelled", "canceled"].includes(event.status)) return false;
    const start = String(event.date_start || event.date || event.scheduled_date || event.due_date || "").slice(0, 10);
    const end = String(event.date_end || start).slice(0, 10);
    return Boolean(start && start <= date && date <= end) || String(event.due_date || "").slice(0, 10) === date;
};

export const mergeClassroomEvents = (localEvents, remoteEvents, childId) => {
    const localById = new Map(localEvents.map((event) => [eventKey(event), event]));
    const remote = remoteEvents.map(normalizeDashboardEvent);
    const remoteIds = new Set(remote.map(eventKey));
    const retained = localEvents.filter((event) => !remoteIds.has(eventKey(event)) &&
        !(String(event.child_id) === String(childId) && event.event_id));
    return [...retained, ...remote.map((event) => {
        const local = localById.get(eventKey(event));
        // A calendar move or absence recovery can be newer than the server copy.
        const localTime = Date.parse(local?.updated_at || local?.recovery_moved_at || "") || 0;
        const remoteTime = Date.parse(event.updated_at || "") || 0;
        const chosen = !event.deleted_from_dashboard && local && localTime > remoteTime ? normalizeDashboardEvent(local) : event;
        const timing = event.auto_class_timing ? Object.fromEntries(["auto_class_timing", "start_time", "end_time", "scheduled_start_time", "scheduled_end_time", "all_day", "duration_minutes", "class_slot_order", "timing_conflict", "timer_flow_id", "timer_step_order", "assignment_time_slots"].map((key) => [key, event[key]])) : {};
        return { ...chosen, ...timing, follow_up: event.follow_up, excused: event.excused, student_progress: event.student_progress, postponement_requests: event.postponement_requests,
            parent_review: event.parent_review, academic_grade_level: event.academic_grade_level, report_archived_through: event.report_archived_through,
            data: { ...chosen.data, ...timing, follow_up: event.follow_up, excused: event.excused, student_progress: event.student_progress, postponement_requests: event.postponement_requests,
                parent_review: event.parent_review, academic_grade_level: event.academic_grade_level, report_archived_through: event.report_archived_through } };
    })];
};

export const eventIsComplete = (event) =>
    event.status === "completed" || event.status === "graded" || event.completed === true;

// Calendar dates are already materialized by the classroom and recovery tools.
// Read them directly so moves, absence recovery and explicit holiday assignments agree.
export const assignmentsForDay = (events, date, childIds, childId = "", subjectId = "") => {
    const allowed = new Set(childIds.map(String));
    const seen = new Set();
    return events.map(normalizeDashboardEvent).filter((event) => {
        const key = eventKey(event);
        if (event.id == null || seen.has(key) || !allowed.has(String(event.child_id))) return false;
        if (event.archived || event.is_archived || ["cancelled", "canceled"].includes(event.status)) return false;
        if (event.report_archived_through && event.report_archived_through >= date) return false;
        if (childId && String(event.child_id) !== String(childId)) return false;
        if (subjectId && String(event.subject_id) !== String(subjectId)) return false;
        const start = String(event.date_start || event.date || event.due_date || event.scheduled_date || "").slice(0, 10);
        const end = String(event.date_end || start).slice(0, 10);
        if (!(start && start <= date && date <= end) && String(event.due_date || "").slice(0, 10) !== date) return false;
        seen.add(key);
        return true;
    }).sort((a, b) => String(a.scheduled_start_time || a.start_time || a.due_time || "").localeCompare(
        String(b.scheduled_start_time || b.start_time || b.due_time || "")
    ) || String(a.title || "").localeCompare(String(b.title || "")));
};

export const reorderById = (items, sourceId, targetId) => {
    const source = items.findIndex((item) => item.id === sourceId);
    const target = items.findIndex((item) => item.id === targetId);
    if (source < 0 || target < 0 || source === target) return items;
    const next = [...items];
    const [item] = next.splice(source, 1);
    next.splice(target, 0, item);
    return next;
};

export const safeGadgetLink = (value) => {
    try {
        const url = new URL(value);
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
    } catch {
        return "";
    }
};

export const schoolNoticesForDay = (calendar, date) =>
    ["holidays", "vacations", "daysOff", "items"].flatMap((type) =>
        (Array.isArray(calendar?.[type]) ? calendar[type] : []).filter((item) => {
            if (type === "items" && !item.blocksSchool) return false;
            const start = typeof item === "string" ? item : item.start || item.date_start || item.date;
            const end = typeof item === "string" ? item : item.end || item.date_end || start;
            return start && start <= date && date <= end;
        }).map((item) => typeof item === "string" ? "School day off" : item.name || item.title || (type === "vacations" ? "Vacation" : "School day off"))
    );

export const scheduledClassesForDay = (subjects, date, readSchedule) => {
    const weekday = new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" });
    return subjects.filter((subject) => {
        const schedule = readSchedule(subject.subject_id ?? subject.id) || subject.class_schedule;
        if (!schedule || schedule.mode === "block" || schedule.mode === "flexible") return false;
        return Array.isArray(schedule.days) && schedule.days.includes(weekday);
    });
};

export const studentCompleted = (event) => Boolean(event.student_progress?.completed || event.data?.student_progress?.completed);

export const classroomCardsForDay = ({ subjects, children, events, date, calendar = {}, childId = "", block = "", readSchedule = () => null }) => {
    const dayEvents = assignmentsForDay(events, date, children.map((child) => child.child_id), childId).map((event) => ({ ...event, ...event.assignment_time_slots?.[date] })).sort((a, b) => (a.class_slot_order ?? 100000) - (b.class_slot_order ?? 100000));
    const weekday = new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" });
    return subjects.flatMap((subject) => {
        const subjectId = String(subject.subject_id ?? subject.id);
        const child = children.find((item) => String(item.child_id) === String(subject.child_id));
        if (!child || (childId && String(child.child_id) !== childId) || subject.is_archived) return [];
        const ownCalendar = Object.fromEntries(["holidays", "vacations", "daysOff", "items"].map((key) => [key,
            (Array.isArray(calendar[key]) ? calendar[key] : []).filter((item) => !item.child_id || String(item.child_id) === String(child.child_id)),
        ]));
        const dayOff = schoolNoticesForDay(ownCalendar, date).length > 0;
        const schedule = subject.class_schedule?.mode ? subject.class_schedule : readSchedule(subjectId) || subject.class_schedule || {};
        const isBlock = ["block", "flexible"].includes(schedule.mode);
        const blockLabel = isBlock ? (schedule.preferred_block === "custom" ? schedule.custom_block_label || "Custom block" : schedule.preferred_block || "Flexible") : "Scheduled";
        const blockId = isBlock ? blockLabel.toLowerCase() : "";
        if (block && blockId !== block) return [];
        const assignments = dayEvents.filter((event) => String(event.subject_id) === subjectId && String(event.child_id) === String(child.child_id));
        const days = Array.isArray(schedule.days) ? schedule.days : [];
        const scheduled = isBlock ? (days.length ? days.includes(weekday) : !["Saturday", "Sunday"].includes(weekday)) : days.includes(weekday);
        if (!scheduled || dayOff) return [];
        return [{ id: `${child.child_id}:${subjectId}`, subjectId, subject, child, isBlock, blockId,
            blockLabel: blockLabel.charAt(0).toUpperCase() + blockLabel.slice(1), assignments, color: subject.color || "#c084fc" }];
    });
};

export const assignmentRequiresGrade = (event) => event.grading_mode === "graded" || (!event.grading_mode && (event.is_graded === true || event.grade != null));
export const assignmentStage = (event) => {
    if (["graded", "approved"].includes(event.parent_review?.state) || (event.grade != null && ["graded", "completed"].includes(event.status)) || event.status === "completed") return "graded";
    if (["pending_approval", "submitted"].includes(event.status) || studentCompleted(event)) return "pending";
    return "active";
};

// Keep timed classes in place while allowing block cards to be arranged freely.
export const orderClassroomCards = (cards, layouts, date) => {
    const byChild = new Map();
    cards.filter((card) => card.isBlock).forEach((card) => {
        const childId = String(card.child.child_id);
        if (!byChild.has(childId)) byChild.set(childId, []);
        byChild.get(childId).push(card);
    });
    byChild.forEach((items, childId) => {
        const order = layouts[childId]?.find((layout) => layout.date === date)?.order || [];
        items.sort((a, b) => {
            const rank = (id) => { const index = order.indexOf(id); return index < 0 ? Number.MAX_SAFE_INTEGER : index; };
            return rank(a.subjectId) - rank(b.subjectId);
        });
    });
    return cards.map((card) => card.isBlock ? byChild.get(String(card.child.child_id)).shift() : card);
};
